// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Deal, DealStatuses } from '@interfaces/deal.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { DealsService } from '@services/deals.service';
import { ErrorService } from '@services/error.service';
import { NavService } from '@services/nav.service';
import { catchError, lastValueFrom, takeWhile } from 'rxjs';
import { AuditTrailModalComponent } from 'src/app/modals/audit-trail-modal/audit-trail-modal.component';
import { DealershipService } from '../../services/dealership.service';
import { SettingsService } from '@services/settings.service';

@Component({
    selector: 'breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent implements OnDestroy, OnInit {
    public breadcrumbs$ = this.navService.getBreadCrumbs$();
    public pageActions$ = this.navService.pageActions$;
    private currentDeal: Deal = null;
    public currentDealStatus: string | DealStatuses = null;
    private isComponentAlive = true;
    public pageTitle = this.navService.pageTitle;
    public headerTooltip = this.navService.headerTooltip;
    @Output() public outEvent: EventEmitter<AppEvent> = new EventEmitter();
    tenantSupportPhone = '';
    public customAuditTitle: string;

    constructor(
        private readonly dialog: MatDialog,
        private readonly dealsService: DealsService,
        private readonly errorService: ErrorService,
        private readonly navService: NavService,
        private readonly router: Router,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {
        //We need this below check to instantiate a value, otherwise on page refresh no matter which route you're on you will get the default value of Deal Jacket ID
        if (this.router.url.includes('/storage')) {
            this.customAuditTitle = 'Storage Jacket ID';
        } else if (this.router.url.includes('/documents')) {
            this.customAuditTitle = 'Document Jacket ID';
        } else {
            this.customAuditTitle = 'Deal Jacket ID';
        }

        this.router.events.pipe(takeWhile(() => this.isComponentAlive)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                if (event.url.includes('/storage')) {
                    this.customAuditTitle = 'Storage Jacket ID';
                } else if (event.url.includes('/documents')) {
                    this.customAuditTitle = 'Document Jacket ID';
                } else {
                    this.customAuditTitle = 'Deal Jacket ID';
                }

                // Need this final slash to indicate we are on an individual deal page, not the deals page
                if (event instanceof NavigationEnd) {
                    this.performDealRouteCheck(event.url);
                }
            }
        });
    }

    ngOnInit(): void {
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            ({ supportPhone: this.tenantSupportPhone } = await this.settingsService.getOemSettings(dealer.oemCode));
        });

        this.performDealRouteCheck(this.router.url);
    }

    private performDealRouteCheck(url: string) {
        if (url.includes('/dashboard/deals/') || url.includes('/dashboard/documents/') || url.includes('/dashboard/storage/')) {
            // make sure we are not adding document status to any other routes access inside 'deals' route
            if (
                !url.includes('add') &&
                !url.includes('archived') &&
                !url.includes('closed') &&
                !url.includes('create') &&
                !url.includes('merge') &&
                !url.includes('notarize') &&
                !url.includes('sign') &&
                !url.includes('upload') &&
                !url.includes('view') &&
                !url.includes('send')
            ) {
                this.getDeal(url);
            }
        } else {
            // clear deal/dealStatus on all other route changes
            this.currentDeal = null;
            this.currentDealStatus = null;
        }
    }

    private async getDeal(url: string): Promise<void> {
        // get dealId from route
        const dealId = url.split('/').pop();

        // get deal information
        if (dealId) {
            this.currentDeal = await lastValueFrom(this.dealsService.getDeal$(parseInt(dealId)));

            // get the status of the deal to display next to the title
            Object.entries(DealStatuses).forEach(([key, value]) => {
                if (this.currentDeal.statusId === parseInt(key)) {
                    this.currentDealStatus = value;
                }
            });
        }
    }

    public viewAuditTrail(): void {
        // tell parent component to start loader while we get deal audit trail
        this.outEvent.emit({
            context: 'loader',
            data: true,
        });

        this.dealsService
            .getAuditTrail(this.currentDeal.dealerId, this.currentDeal.dealId, true)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showError(
                        `We don't have any data for this audit trail. If you think this is an error, please contact ${this.tenantSupportPhone}`
                    );

                    // tell parent component to stop loader before opening modal
                    this.outEvent.emit({
                        context: 'loader',
                        data: false,
                    });
                    throw error;
                })
            )
            .subscribe((trail) => {
                // tell parent component to stop loader before opening modal
                this.outEvent.emit({
                    context: 'loader',
                    data: false,
                });
                this.dialog.open(AuditTrailModalComponent, {
                    width: '900px',
                    closeOnNavigation: true,
                    data: {
                        deal: this.currentDeal,
                        trail,
                        uniqueTitle: this.customAuditTitle,
                    },
                });
            });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
